import { CalendarPlus, CheckSquare } from 'lucide-react';
import * as React from 'react';
import type { Task, TaskStatusMeta, WorkspaceMember } from '../../lib/task-list';
import { Calendar as DatePicker } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { TaskCard } from '../tasks/TaskCard';

interface UnscheduledTasksPanelProps {
  tasks: Task[];
  workspaceMembers?: WorkspaceMember[];
  /** Workspace task statuses, so each card shows the configured status label and colour. */
  statuses?: TaskStatusMeta[];
  isLoading?: boolean;
  isReadOnly?: boolean;
  onTaskClick: (taskId: number) => void;
  onSchedule: (taskId: number, date: Date) => void;
}

function UnscheduledTaskRow({
  task,
  workspaceMembers,
  statuses,
  isReadOnly,
  onTaskClick,
  onSchedule,
}: {
  task: Task;
  workspaceMembers?: WorkspaceMember[];
  statuses: TaskStatusMeta[];
  isReadOnly?: boolean;
  onTaskClick: (taskId: number) => void;
  onSchedule: (taskId: number, date: Date) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <TaskCard
      task={task}
      statuses={statuses}
      members={workspaceMembers ?? []}
      onClick={() => onTaskClick(task.id)}
      className="group"
      actionSlot={
        isReadOnly ? undefined : (
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground opacity-0 transition-all group-hover:opacity-100 hover:bg-primary/10 hover:text-primary"
                title="Schedule"
              >
                <CalendarPlus className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end" onClick={(e) => e.stopPropagation()}>
              <DatePicker
                mode="single"
                selected={undefined}
                onSelect={(date) => {
                  if (!date) return;
                  onSchedule(task.id, date);
                  setIsOpen(false);
                }}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        )
      }
    />
  );
}

export function UnscheduledTasksPanel({
  tasks,
  workspaceMembers,
  statuses = [],
  isLoading,
  isReadOnly,
  onTaskClick,
  onSchedule,
}: UnscheduledTasksPanelProps) {
  return (
    <aside className="w-80 shrink-0 flex flex-col h-full min-h-0">
      <div className="pb-3 shrink-0">
        <h2 className="text-base font-semibold tracking-tight">Unscheduled Tasks</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Hover a task and pick a date to put it on the calendar.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2.5 scrollbar-thin pr-1">
        {isLoading ? (
          Array(3)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="h-20 rounded-xl border border-border/40 bg-card/40 animate-pulse"
              />
            ))
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12 px-4 rounded-xl border border-dashed border-border/60 gap-2">
            <CheckSquare className="h-6 w-6 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">All caught up</p>
            <p className="text-xs text-muted-foreground">Every task has a due date.</p>
          </div>
        ) : (
          tasks.map((task) => (
            <UnscheduledTaskRow
              key={task.id}
              task={task}
              workspaceMembers={workspaceMembers}
              statuses={statuses}
              isReadOnly={isReadOnly}
              onTaskClick={onTaskClick}
              onSchedule={onSchedule}
            />
          ))
        )}
      </div>
    </aside>
  );
}
